import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Alert, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeatureGuard from '../components/common/FeatureGuard';
import { useDecodedToken } from '../utils/useDecodedToken';
import UserTypeIndicator from '../components/common/UserTypeIndicator';
import { api } from '../api/client';
import { isAuthenticated } from '../utils/auth';

const Transactions: React.FC = () => {
  const user = useDecodedToken();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchTransactions = async () => {
    if (!isAuthenticated()) {

      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const accountNumber = user?.accountNumber || '';
      if (!accountNumber) {
        setError('Account number not found');
        setLoading(false);
        return;
      }

      // Get client accounts to find loan IDs
      const accountsRes = await api.get(`/api/clients/${accountNumber}/accounts`);
      const loanAccounts = accountsRes.data.loanAccounts || [];

      
      // Fetch transactions for each loan
      const allTransactions: any[] = [];
      for (const loan of loanAccounts) {
        try {
          const loanTransactionsRes = await api.get(`/api/loans/${loan.id}/associations?associations=transactions`);

          
          // Check for transactions in different possible locations
          let loanTransactions = loanTransactionsRes.data.transactions || [];
          
          // If not found in data.transactions, check if the entire response is the transactions array
          if (!Array.isArray(loanTransactions) || loanTransactions.length === 0) {
            if (Array.isArray(loanTransactionsRes.data)) {
              loanTransactions = loanTransactionsRes.data;
            }
          }
          

          
          if (Array.isArray(loanTransactions) && loanTransactions.length > 0) {
            allTransactions.push(...loanTransactions);
          }
        } catch (loanErr: any) {
          console.error(`Failed to fetch transactions for loan ${loan.id}:`, loanErr);
        }
      }
      

      setTransactions(allTransactions);
      
    } catch (err: any) {
      console.error('Failed to fetch transactions:', err);
      setError('Unable to load transaction data. The external service may be temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [user?.accountNumber]);

  const handleRefresh = () => {
    fetchTransactions();
  };

  const filteredTransactions = transactions.filter((tx: any) => {
    const t = `${tx.type?.value || ''} ${tx.narration || ''} ${tx.description || ''}`.toLowerCase();
    return !t.includes('accrual');
  });

  return (
    <FeatureGuard feature="transactions">
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Transactions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }}
              title="Refresh Transaction Data"
            >
              <RefreshIcon />
            </IconButton>
            <UserTypeIndicator />
          </Box>
        </Box>

        {/* Access Level Information */}
        {!user?.accountNumber && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Access Restricted:</strong> You need to verify your SMEDCO account number to view transaction history. 
              You can still create and manage loan applications from the Loan Applications page.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Recent Transactions
              {loading && <CircularProgress size={20} />}
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTransactions.length === 0 ? (
              <Typography>No recent transactions found.</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f0f4f8' }}>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Type</b></TableCell>
                      <TableCell><b>Amount</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Description</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.map((tx, index) => (
                      <TableRow key={tx.id || index} hover>
                        <TableCell>
                          {tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: (tx.amount || 0) >= 0 ? 'success.main' : 'error.main'
                            }}
                          >
                            ${(tx.amount || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={tx.manuallyReversed ? 'Reversed' : 'Active'} 
                            color={tx.manuallyReversed ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </FeatureGuard>
  );
};

export default Transactions; 



